import React, { useEffect, useState } from "react";
import { api, formatErr, downloadCsv } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { TrendingUp, Download, AlertCircle, UserCheck } from "lucide-react";

// Color-code the return rate: <3% green, <8% amber, ≥8% red
const rateClass = (r) => r < 3 ? "text-emerald-600" : r < 8 ? "text-amber-600" : "text-red-600";
const rateLabel = (r) => r < 3 ? "ممتاز" : r < 8 ? "مقبول" : "مرتفع — راجع الأداء";

export default function SalesRepReturns() {
  const [from, setFrom] = useState(() => new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/sales-rep-returns", { params: { from_date: from, to_date: to } });
      setData(d);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]); // eslint-disable-line

  const exportCsv = () => {
    if (!data?.reps?.length) return;
    const rows = [
      [`تقرير معدل الإرجاع للمندوبين — ${from} إلى ${to}`],
      ["المندوب", "عدد المبيعات", "إجمالي المبيعات", "عدد المرتجعات", "إجمالي المرتجعات", "الصافي", "معدل الإرجاع %"],
      ...data.reps.map(r => [r.name, r.sales_count, r.sales_total, r.returns_count, r.returns_total, r.net_total, r.return_rate]),
    ];
    downloadCsv(`sales_rep_returns_${from}_${to}.csv`, rows);
    toast.success("تم التصدير");
  };

  return (
    <div className="space-y-6" data-testid="sales-rep-returns-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-blue-600" /> معدل الإرجاع للمندوبين
          </h1>
          <p className="text-sm text-muted-foreground mt-1">اكتشف من يبيع كثيراً ثم يعود — معدل مرتفع = مبالغة في الوعود</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">من</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="from-date-input" />
          </div>
          <div>
            <Label className="text-xs">إلى</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="to-date-input" />
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={!data?.reps?.length} className="gap-1 h-9" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> تصدير
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-16 text-sm">جاري التحميل...</div>
      ) : !data?.reps?.length ? (
        <Card className="p-12 text-center border-dashed">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <div className="text-muted-foreground">لا توجد بيانات مندوبين في هذه الفترة</div>
          <div className="text-xs text-muted-foreground/70 mt-1">تأكد أن الفواتير تحتوي على `sales_rep_id`</div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-start">المندوب</TableHead>
                <TableHead className="text-end">مبيعات</TableHead>
                <TableHead className="text-end">مرتجعات</TableHead>
                <TableHead className="text-end">الصافي</TableHead>
                <TableHead className="text-end">معدل الإرجاع</TableHead>
                <TableHead className="text-start">التقييم</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.reps.map((r, i) => (
                <TableRow key={r.id} data-testid={`rep-row-${i}`}>
                  <TableCell className="font-semibold flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-mono">#{i + 1}</span>
                    {r.name}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    <div className="font-bold">{r.sales_total.toLocaleString("ar-EG")}</div>
                    <div className="text-[10px] text-muted-foreground">{r.sales_count} فاتورة</div>
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    <div className="font-bold text-amber-700">{r.returns_total.toLocaleString("ar-EG")}</div>
                    <div className="text-[10px] text-muted-foreground">{r.returns_count} مرتجع</div>
                  </TableCell>
                  <TableCell className="text-end tabular-nums font-bold text-emerald-700">{r.net_total.toLocaleString("ar-EG")}</TableCell>
                  <TableCell className={`text-end tabular-nums text-xl font-bold ${rateClass(r.return_rate)}`}>{r.return_rate.toFixed(1)}%</TableCell>
                  <TableCell><span className={`text-xs font-bold ${rateClass(r.return_rate)}`}>{rateLabel(r.return_rate)}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
